import React, { lazy } from 'react';
import { FadeIn } from './FadeIn';
import { MapPinIcon, NavigationIcon, PhoneIcon, ClockIcon } from 'lucide-react';
interface LocationProps {
  lang: 'en' | 'es';
}
const GOOGLE_MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2986.5!2d-73.0444!3d41.5681!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e7c1a1a1a1a1a1%3A0x1234567890abcdef!2s1407+N+Main+St%2C+Waterbury%2C+CT+06704!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus';
const GOOGLE_MAPS_DIRECTIONS_URL = 'https://www.google.com/maps/dir/?api=1&destination=1407+North+Main+St,+Waterbury,+CT+06704';
export function Location({
  lang
}: LocationProps) {
  return <section className="bg-brand-dark py-20 md:py-28 relative overflow-hidden" data-id="element-201">
      <div className="max-w-6xl mx-auto px-6" data-id="element-202">
        <FadeIn data-id="element-203">
          <div className="text-center mb-12" data-id="element-204">
            <span className="text-silver tracking-[0.25em] text-xs font-sans font-medium uppercase block mb-4 text-[#C0C0C0]" data-id="element-205">
              {lang === 'es' ? 'Encuéntranos' : 'Find Us'}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl text-[#E8E8E8] leading-tight" data-id="element-206">
              {lang === 'es' ? <>
                  Visítanos en{' '}
                  <span className="text-[#C0C0C0] italic" data-id="element-207">Nuestro Salón</span>
                </> : <>
                  Visit Us at{' '}
                  <span className="text-[#C0C0C0] italic" data-id="element-208">Our Salon</span>
                </>}
            </h2>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-8 items-stretch" data-id="element-209">
          {/* Map */}
          <FadeIn direction="left" delay={0.1} data-id="element-210">
            <div className="relative w-full h-full min-h-[250px] md:min-h-[300px] rounded-sm overflow-hidden border border-brand-dark/10 shadow-lg" data-id="element-211">
              <iframe src={GOOGLE_MAPS_EMBED_URL} width="100%" height="100%" style={{
              border: 0,
              position: 'absolute',
              inset: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={lang === 'es' ? 'Ubicación de ISASHY Beauty Center' : 'ISASHY Beauty Center Location'} data-id="element-212" />
            </div>
          </FadeIn>

          {/* Info Card */}
          <FadeIn direction="right" delay={0.2} data-id="element-213">
            <div className="bg-brand-dark text-brand-cream p-6 md:p-8 rounded-sm flex flex-col justify-between h-full min-h-[250px]" data-id="element-214">
              <div data-id="element-215">
                <h3 className="font-serif text-2xl md:text-3xl mb-6 text-brand-gold" data-id="element-216">
                  ISASHY Beauty Center
                </h3>

                <div className="space-y-5" data-id="element-217">
                  <div className="flex items-start gap-4" data-id="element-218">
                    <MapPinIcon className="w-5 h-5 text-brand-gold mt-0.5 flex-shrink-0" data-id="element-219" />
                    <div data-id="element-220">
                      <p className="font-sans text-sm text-brand-cream/50 uppercase tracking-wider mb-1" data-id="element-221">
                        {lang === 'es' ? 'Dirección' : 'Address'}
                      </p>
                      <p className="font-sans text-brand-cream/90" data-id="element-222">
                        1407 North Main St
                        <br data-id="element-223" />
                        Waterbury, CT 06704
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-224">
                    <PhoneIcon className="w-5 h-5 text-brand-gold mt-0.5 flex-shrink-0" data-id="element-225" />
                    <div data-id="element-226">
                      <p className="font-sans text-sm text-brand-cream/50 uppercase tracking-wider mb-1" data-id="element-227">
                        {lang === 'es' ? 'Teléfono' : 'Phone'}
                      </p>
                      <a href="tel:9172090303" className="font-sans text-brand-cream/90 hover:text-brand-gold transition-colors" data-id="element-228">
                        (917) 209-0303
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-229">
                    <ClockIcon className="w-5 h-5 text-brand-gold mt-0.5 flex-shrink-0" data-id="element-230" />
                    <div data-id="element-231">
                      <p className="font-sans text-sm text-brand-cream/50 uppercase tracking-wider mb-1" data-id="element-232">
                        {lang === 'es' ? 'Horario' : 'Hours'}
                      </p>
                      <p className="font-sans text-brand-cream/90" data-id="element-233">
                        {lang === 'es' ? 'Lun – Sáb: 9am – 7pm' : 'Mon – Sat: 9am – 7pm'}
                        <br data-id="element-234" />
                        <span className="text-brand-cream/50" data-id="element-235">
                          {lang === 'es' ? 'Dom: Cerrado' : 'Sun: Closed'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <a href={GOOGLE_MAPS_DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" className="mt-8 flex items-center justify-center gap-3 bg-brand-gold hover:bg-brand-gold/90 text-brand-dark px-8 py-4 font-sans font-medium tracking-wide transition-all duration-300 transform hover:-translate-y-0.5 rounded-sm" data-id="element-236">
                <NavigationIcon className="w-5 h-5" data-id="element-237" />
                {lang === 'es' ? 'Cómo Llegar' : 'Get Directions'}
              </a>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}